import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import ProfileCard from '../components/ProfileCard';
import { useDispatch } from 'react-redux';
import { refreshAuthProfile } from '../store/slices/authSlice';
import { CircularProgress, Typography, Box, Container } from '@mui/material';
import PageLayout from '../components/layout/PageLayout';

const mapProfileToUser = (profile) => {
  const fullName = profile?.name ?? profile?.user?.name ?? '';
  const [firstName, ...lastNameParts] = (typeof fullName === 'string' ? fullName : '').split(' ');
  return {
    firstName: firstName || '',
    lastName: (lastNameParts || []).join(' ') || '',
    username: profile?.username || '',
    email: profile?.email || '',
    role: profile?.roles?.[0]?.name || profile?.role?.name || 'N/A',
    userId: profile?.id || '',
    joinDate: profile?.created_at || 'N/A',
    avatarUrl: profile?.avatar?.path || 'https://placehold.co/150x150/007FFF/FFFFFF?text=User',
    avatarId: profile?.avatar?.id || '',
    phone: profile?.phone || '',
    gender: profile?.gender || '',
    status: profile?.status?.name || 'N/A',
    dob: profile?.dob || '',
  };
};

const ProfilePage = () => {
  const dispatch = useDispatch();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      setIsLoading(true);
      try {
        const userData = await dispatch(refreshAuthProfile());
        const profile = userData?.user ?? userData?.data ?? userData;
        setUser(mapProfileToUser(profile));
      } catch (error) {
        console.error('Fetch profile error:', error);
        toast.error('Failed to load profile data.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [dispatch]);

  const refetchProfile = useCallback(async () => {
    try {
      const userData = await dispatch(refreshAuthProfile());
      const profile = userData?.user ?? userData?.data ?? userData;
      setUser(mapProfileToUser(profile));
    } catch (e) {
      console.error('Refetch profile error:', e);
    }
  }, [dispatch]);

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
          Profile
        </Typography>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh', flexDirection: 'column', gap: 2 }}>
            <CircularProgress color="primary" />
            <Typography color="text.secondary">Loading profile...</Typography>
          </Box>
        ) : !user ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 3 }}>
            <Typography color="error">Failed to load user data.</Typography>
          </Box>
        ) : (
          <ProfileCard user={user} onProfileUpdated={refetchProfile} />
        )}
      </Container>
    </PageLayout>
  );
};

export default ProfilePage;